import {
	adminCancelAllActiveBuilds,
	adminCancelBuild,
	adminCancelCurrentBuild,
	fetchAdminBuilderQueue,
} from '@/api/admin';
import BuildLogViewer from '@/components/build/build-log-viewer';
import BuildStageStepper from '@/components/build/build-stage-stepper';
import BuildStatusBadge from '@/components/build/build-status-badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import type { AdminBuildSkipAction, IAdminBuilderQueueBuild } from '@/entities/admin-builder-queue.entity';
import {
	$buildSawDropperStage,
	$buildSawPayloadEncryptStage,
	selectSawDropperStage,
	selectSawPayloadEncryptStage,
} from '@/entities/build-live.entity';
import { useBuildSubscription } from '@/hooks/use-build-subscription';
import { PageScroll } from '@/layouts/page-layout';
import AdminBuildSkipDialog from '@/pages/admin/builder-queue/admin-build-skip-dialog';
import { ROUTES } from '@/app/routes';
import { useUnit } from 'effector-react';
import { HammerIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

function BuilderHealthCard({
	busy,
	stage,
	workerName,
	cancelled,
}: {
	busy: boolean;
	stage: string | null;
	workerName: string | null;
	cancelled?: boolean;
}) {
	return (
		<div className='rounded-xl border border-border bg-card p-4'>
			<p className='text-sm font-medium'>Статус билдера</p>
			<p className='mt-1 text-sm text-muted-foreground'>
				{busy ? `Занят · ${stage ?? 'unknown'} · ${workerName ?? '—'}` : 'Свободен'}
			</p>
			{busy && cancelled ? (
				<p className='mt-2 text-xs text-amber-600 dark:text-amber-400'>
					Сборка отменена, builder завершает остановку…
				</p>
			) : null}
		</div>
	);
}

function QueueBuildRow({
	build,
	onSkip,
}: {
	build: IAdminBuilderQueueBuild;
	onSkip: (build: IAdminBuilderQueueBuild) => void;
}) {
	return (
		<div className='rounded-xl border border-border bg-card p-3 flex items-start justify-between gap-3'>
			<div className='min-w-0'>
				<div className='flex items-center gap-2 flex-wrap'>
					{build.queuePosition ? (
						<span className='text-xs font-medium text-muted-foreground'>#{build.queuePosition}</span>
					) : null}
					<p className='font-medium truncate'>{build.appName || build.id}</p>
					<BuildStatusBadge build={build} />
				</div>
				<p className='text-xs text-muted-foreground mt-1'>@{build.profileNickname}</p>
				<p className='text-xs text-muted-foreground font-mono break-all'>{build.id}</p>
				<Link
					to={`${ROUTES.adminUser}${build.profileId}?tab=builds`}
					className='text-xs text-chart-2 hover:underline mt-1 inline-block'
				>
					Профиль пользователя
				</Link>
			</div>
			{build.canAdminCancel ? (
				<Button type='button' size='sm' variant='outline' onClick={() => onSkip(build)}>
					Скипнуть
				</Button>
			) : null}
		</div>
	);
}

export default function AdminBuilderQueuePage() {
	const [loading, setLoading] = useState(true);
	const [refreshing, setRefreshing] = useState(false);
	const [current, setCurrent] = useState<IAdminBuilderQueueBuild | null>(null);
	const [queue, setQueue] = useState<IAdminBuilderQueueBuild[]>([]);
	const [history, setHistory] = useState<IAdminBuilderQueueBuild[]>([]);
	const [historyCursor, setHistoryCursor] = useState<string | null>(null);
	const [builderHealth, setBuilderHealth] = useState<Awaited<ReturnType<typeof fetchAdminBuilderQueue>>['builderHealth']>(null);
	const [skipAction, setSkipAction] = useState<AdminBuildSkipAction | null>(null);
	const [skipBuild, setSkipBuild] = useState<IAdminBuilderQueueBuild | null>(null);
	const [skipDialogOpen, setSkipDialogOpen] = useState(false);
	const [skipLoading, setSkipLoading] = useState(false);
	const [loadingMoreHistory, setLoadingMoreHistory] = useState(false);

	const { progress: liveProgress, logs } = useBuildSubscription(current?.id);
	const sawDropperMap = useUnit($buildSawDropperStage);
	const sawPayloadEncryptMap = useUnit($buildSawPayloadEncryptStage);
	const sawDropperStage = selectSawDropperStage(sawDropperMap, current?.id);
	const sawPayloadEncryptStage = selectSawPayloadEncryptStage(sawPayloadEncryptMap, current?.id);
	const currentStage = liveProgress?.stage ?? current?.builderStage ?? null;

	const applyActiveSnapshot = useCallback((snapshot: Awaited<ReturnType<typeof fetchAdminBuilderQueue>>) => {
		setBuilderHealth(snapshot.builderHealth);
		setCurrent(snapshot.current);
		setQueue(snapshot.queue);
	}, []);

	const applyFullSnapshot = useCallback((snapshot: Awaited<ReturnType<typeof fetchAdminBuilderQueue>>) => {
		applyActiveSnapshot(snapshot);
		setHistory(snapshot.history.items);
		setHistoryCursor(snapshot.history.nextCursor);
	}, [applyActiveSnapshot]);

	const loadQueue = useCallback(
		async (options?: { silent?: boolean; preserveHistory?: boolean }) => {
			if (!options?.silent) {
				setRefreshing(true);
			}
			try {
				const snapshot = await fetchAdminBuilderQueue({ historyLimit: 30 });
				if (options?.preserveHistory) {
					applyActiveSnapshot(snapshot);
				} else {
					applyFullSnapshot(snapshot);
				}
			} finally {
				setLoading(false);
				setRefreshing(false);
			}
		},
		[applyActiveSnapshot, applyFullSnapshot],
	);

	useEffect(() => {
		void loadQueue();
		const intervalId = window.setInterval(() => {
			void loadQueue({ silent: true, preserveHistory: true });
		}, 5000);
		return () => window.clearInterval(intervalId);
	}, [loadQueue]);

	const openSkipDialog = (action: AdminBuildSkipAction, build: IAdminBuilderQueueBuild | null = null) => {
		setSkipAction(action);
		setSkipBuild(build);
		setSkipDialogOpen(true);
	};

	const handleSkipConfirm = async (reason: string) => {
		if (!skipAction) return;
		setSkipLoading(true);
		try {
			if (skipAction === 'single' && skipBuild) {
				await adminCancelBuild(skipBuild.id, reason);
				toast.success('Билд скипнут');
			} else if (skipAction === 'current') {
				await adminCancelCurrentBuild(reason);
				toast.success('Текущая сборка скипнута');
			} else if (skipAction === 'all') {
				const result = await adminCancelAllActiveBuilds(reason);
				if (result.failures.length === 0) {
					toast.success(`Скипнуто билдов: ${result.cancelledCount}`);
				} else {
					const failurePreview = result.failures
						.slice(0, 2)
						.map(f => `${f.buildId}: ${f.message}`)
						.join('; ');
					const suffix = result.failures.length > 2 ? '…' : '';
					toast.warning(
						`Скипнуто ${result.cancelledCount}, ошибок ${result.failures.length}. ${failurePreview}${suffix}`,
					);
				}
			}
			setSkipDialogOpen(false);
			await loadQueue({ silent: true });
		} catch {
			// toast.error from http interceptor
		} finally {
			setSkipLoading(false);
		}
	};

	const loadMoreHistory = async () => {
		if (!historyCursor || loadingMoreHistory) return;
		setLoadingMoreHistory(true);
		try {
			const snapshot = await fetchAdminBuilderQueue({
				historyCursor,
				historyLimit: 30,
			});
			setHistory(prev => [...prev, ...snapshot.history.items]);
			setHistoryCursor(snapshot.history.nextCursor);
		} finally {
			setLoadingMoreHistory(false);
		}
	};

	const hasActive = Boolean(current) || queue.length > 0;

	if (loading) {
		return (
			<div className='flex justify-center py-12'>
				<Spinner />
			</div>
		);
	}

	return (
		<PageScroll contentClassName='p-2 flex flex-col gap-4'>
			<div className='flex items-start justify-between gap-3 flex-wrap'>
				<div className='flex items-center gap-3'>
					<div className='rounded-lg bg-chart-5/15 p-2'>
						<HammerIcon className='text-chart-5' size={20} />
					</div>
					<div>
						<h1 className='text-base font-semibold text-chart-5'>Очередь билдера</h1>
						<p className='text-xs text-muted-foreground'>Текущая сборка, очередь и история</p>
					</div>
				</div>
				<div className='flex gap-2 flex-wrap'>
					<Button
						type='button'
						size='sm'
						variant='outline'
						disabled={!current || refreshing}
						onClick={() => openSkipDialog('current', current)}
					>
						Скипнуть текущий
					</Button>
					<Button
						type='button'
						size='sm'
						variant='destructive'
						disabled={!hasActive || refreshing}
						onClick={() => openSkipDialog('all')}
					>
						Скипнуть всё
					</Button>
				</div>
			</div>

			<BuilderHealthCard
				busy={Boolean(builderHealth?.busy)}
				stage={builderHealth?.stage ?? null}
				workerName={builderHealth?.workerName ?? null}
				cancelled={Boolean(
					builderHealth?.cancelled || (builderHealth?.busy && current?.cancelled),
				)}
			/>

			<section className='flex flex-col gap-2'>
				<h2 className='text-sm font-medium'>Сейчас собирается</h2>
				{current ? (
					<div className='rounded-xl border border-chart-5/30 bg-chart-5/10 p-4 flex flex-col gap-3'>
						<div className='flex items-start justify-between gap-3'>
							<div className='min-w-0'>
								<p className='font-medium'>{current.appName || current.id}</p>
								<p className='text-xs text-muted-foreground'>@{current.profileNickname}</p>
								<p className='text-xs text-muted-foreground font-mono break-all'>{current.id}</p>
							</div>
							<div className='flex items-center gap-2'>
								<BuildStatusBadge build={current} />
								{current.canAdminCancel ? (
									<Button type='button' size='sm' variant='outline' onClick={() => openSkipDialog('single', current)}>
										Скипнуть
									</Button>
								) : null}
							</div>
						</div>
						<BuildStageStepper
							currentStage={currentStage}
							sawDropperStage={sawDropperStage}
							sawPayloadEncryptStage={sawPayloadEncryptStage}
							skipEncrypt={Boolean(current.skipEncrypt)}
						/>
						<BuildLogViewer lines={logs} />
					</div>
				) : (
					<div className='rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground'>
						Нет активной сборки
					</div>
				)}
			</section>

			<section className='flex flex-col gap-2'>
				<h2 className='text-sm font-medium'>Очередь ({queue.length})</h2>
				{queue.length === 0 ? (
					<div className='rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground'>
						Очередь пуста
					</div>
				) : (
					queue.map(build => (
						<QueueBuildRow key={build.id} build={build} onSkip={item => openSkipDialog('single', item)} />
					))
				)}
			</section>

			<section className='flex flex-col gap-2'>
				<h2 className='text-sm font-medium'>История</h2>
				{history.length === 0 ? (
					<div className='rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground'>
						История пуста
					</div>
				) : (
					<div className='flex flex-col gap-2'>
						{history.map(build => (
							<div key={build.id} className='rounded-xl border border-border bg-card p-3'>
								<div className='flex items-start justify-between gap-2'>
									<div className='min-w-0'>
										<div className='flex items-center gap-2 flex-wrap'>
											<p className='font-medium truncate'>{build.appName || build.id}</p>
											<BuildStatusBadge build={build} />
										</div>
										<p className='text-xs text-muted-foreground mt-1'>@{build.profileNickname}</p>
										<p className='text-xs text-muted-foreground font-mono break-all'>{build.id}</p>
										{build.failureMessage ? (
											<p className='text-xs text-destructive mt-2 break-words'>{build.failureMessage}</p>
										) : null}
									</div>
									<Link
										to={`${ROUTES.adminUser}${build.profileId}?tab=builds`}
										className='text-xs text-chart-2 hover:underline shrink-0'
									>
										Профиль
									</Link>
								</div>
							</div>
						))}
						{historyCursor ? (
							<Button
								type='button'
								variant='outline'
								className='w-full'
								disabled={loadingMoreHistory}
								onClick={() => void loadMoreHistory()}
							>
								{loadingMoreHistory ? <Spinner className='size-4' /> : 'Загрузить ещё'}
							</Button>
						) : null}
					</div>
				)}
			</section>

			<AdminBuildSkipDialog
				open={skipDialogOpen}
				onOpenChange={setSkipDialogOpen}
				action={skipAction}
				build={skipBuild}
				onConfirm={handleSkipConfirm}
				loading={skipLoading}
			/>
		</PageScroll>
	);
}
